import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { EmptyState } from '@/components/common/empty-state';
import {
  ProductDetailHero,
  ProductDetailHeroSkeleton,
} from '@/features/mercado/components';
import { useListing, useCreateThread } from '@/features/mercado/api';
import { useCondoContext } from '@/features/auth/use-condo-context';
import { MOCK_TENANT_ID } from '@/lib/auth';
import { PageContainer } from '@/components/common/page-container';
import { PageHeader } from '@/components/common/page-header';

export function MercadoDetailRoute() {
  const { listingId } = useParams<{ listingId: string }>();
  const navigate = useNavigate();
  const { condoId, rol } = useCondoContext();
  const tenantId = condoId || MOCK_TENANT_ID;

  const isResident = rol === 'residente';

  const { data: listing, isLoading } = useListing(listingId ?? '');
  const createThread = useCreateThread();

  async function handleContact() {
    if (!listing) return;
    try {
      const thread = await createThread.mutateAsync({
        listingId: listing.id,
        message: `Hola, me interesa tu "${listing.title}". Esta disponible?`,
      });
      navigate(`/app/condominios/${condoId}/mercado/chats/${thread.id}`);
    } catch {
      toast.error('No se pudo iniciar la conversacion');
    }
  }

  if (isLoading) {
    return (
      <PageContainer>
        <PageHeader
          eyebrow="Comunidad · Mercado Vecinal"
          title="Cargando..."
          noRule
        />
        <ProductDetailHeroSkeleton />
      </PageContainer>
    );
  }

  if (!listing) {
    return (
      <PageContainer>
        <EmptyState
          title="Aviso no encontrado"
          description="El articulo que buscas no existe o fue eliminado."
          action={
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate(`/app/condominios/${condoId}/mercado`)}
            >
              Volver al mercado
            </Button>
          }
        />
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <PageHeader
        eyebrow="Comunidad · Mercado Vecinal"
        title={listing.title}
        action={
          <Button
            variant="ghost"
            onClick={() => navigate(`/app/condominios/${condoId}/mercado`)}
          >
            <ArrowLeft className="h-4 w-4" />
            Volver
          </Button>
        }
      />

      <ProductDetailHero
        images={listing.photos.map((p) => p.url)}
        title={listing.title}
        priceCLP={listing.priceCLP}
        condition={listing.condition}
        description={listing.description ?? ''}
        seller={{
          name: listing.sellerName,
          rating: 4.5,
          reviewCount: 12,
          isSameBuilding: listing.sellerTenantId === tenantId,
          condominiumName: listing.condominiumName,
        }}
        createdAt={listing.createdAt}
        viewCount={42}
        onContact={isResident ? handleContact : undefined}
        onViewSellerProfile={() =>
          navigate(`/app/condominios/${condoId}/mercado/vendedor/${listing.sellerId}`)
        }
      />
    </PageContainer>
  );
}
